<template>
  <div class="flex flex-col" data-test="howWeWorkedOutIncomeProjectionModal">
    <BeatLoader
      v-if="loadingAssumption"
      class="mt-[40px] text-center"
      color="var(--color-primary)"
      size="32px" />
    <template v-else>
      <h3 class="text-blue-1 mt-[40px]">How we worked this out?</h3>
      <br />
      <p class="paragraph-1">
        We consider your current <span v-if="isCFSWhitelabel()">CFS</span> investment options (if
        they have at least 5 years' history), including their expected returns, tax on earnings, and
        investment and administration fees — all of which are essential to generating an income
        projection.
      </p>
      <div class="flex flex-col py-3">
        <div class="w-full">
          <OtivoTable
            :number-of-columns="4"
            :table-data="tableData"
            class="-mx-2.5"
            data-test="incomeProjectionTable" />
          <br />
          <div class="flex w-full">
            <div class="pt-4 paragraph-2 w-60">
              <label for="inflation-rate">Inflation Rate</label>
            </div>
            <p class="paragraph-2 py-4 px-6" id="inflation-rate">{{ inflationRate }}%</p>
          </div>
          <div class="flex w-full" v-if="annualInsurancePremium">
            <div class="pt-4 paragraph-2 w-60">
              <label for="inflation-rate">Annual Insurance Premium</label>
            </div>
            <p class="paragraph-2 py-4 px-6" id="inflation-rate">
              ${{ annualInsurancePremium.toPrecision(2) }}
            </p>
          </div>
        </div>
      </div>
      <LifeplanChartLegend v-if="displayLifePlanLegend" />
      <div v-html="assumptionHtml"></div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import BeatLoader from 'vue-spinner/src/BeatLoader.vue'
import OtivoTable from '@/components/OtivoTable.vue'
import getGraphAssumptions from '@/services/GraphAssumptionsService.ts'
import retirementService from '@/services/RetirementService.ts'
import { isCFSWhitelabel } from '@/composables/isCFSWhitelabel.ts'
import LifeplanChartLegend from '@/views/Otivo/Dashboard/Lifeplan/LifeplanChartLegend.vue'

const inflationRate = ref(0)
const annualInsurancePremium = ref(0)

withDefaults(
  defineProps<{
    displayLifePlanLegend: boolean
  }>(),
  {
    displayLifePlanLegend: false,
  },
)

const tableHeader = [
  { component: 'Investment Option', fieldType: 'heading' },
  { component: 'Investment return', fieldType: 'heading' },
  { component: 'Tax on earnings', fieldType: 'heading' },
  { component: 'Fees (%)', fieldType: 'heading' },
]
const tableData = ref([])

const assumptionHtml = ref('')
const loadingAssumption = ref(false)
onMounted(async () => {
  try {
    loadingAssumption.value = true
    const { data: graphAssumptions } = await getGraphAssumptions(
      isCFSWhitelabel() ? 'CFS_INCOME_PROJECTION_ASSUMPTIONS' : 'INCOME_PROJECTION_ASSUMPTIONS',
    )
    const { data: investAssumptions } = await retirementService.getRetireInvestAssumptions()
    assumptionHtml.value = graphAssumptions.data?.assumptions || ''
    const { assumptions, cpi } = investAssumptions
    inflationRate.value = cpi
    tableData.value = tableHeader

    tableData.value = tableData.value.concat(
      populateTableData(assumptions, 'Superannuation', 'super'),
    )
    tableData.value = tableData.value.concat(
      populateTableData(assumptions, 'Investment property', 'property'),
    )
    tableData.value = tableData.value.concat(populateTableData(assumptions, 'Shares', 'shares'))
    tableData.value = tableData.value.concat(populateTableData(assumptions, 'Cash', 'cashAndFixed'))
    tableData.value = tableData.value.concat(
      populateTableData(assumptions, 'Managed Funds', 'managedFunds'),
    )
    tableData.value = tableData.value.concat(
      populateTableData(assumptions, 'Alternative', 'alternative'),
    )
  } catch (err) {
    console.log(err)
  } finally {
    loadingAssumption.value = false
  }
})

const generateTableRow = (
  title: string,
  payload: { rateOfReturn: number; taxOnEarnings: number; adminFee: number },
) => {
  const { rateOfReturn, taxOnEarnings, adminFee } = payload
  return [
    { component: title, fieldType: 'html' },
    { component: rateOfReturn != null ? `${rateOfReturn.toPrecision(3)}%` : '', fieldType: 'html' },
    {
      component: taxOnEarnings != null ? `${taxOnEarnings.toPrecision(2)}%` : '',
      fieldType: 'html',
    },
    { component: adminFee ? `${adminFee.toPrecision(3)}%` : '', fieldType: 'html' },
  ]
}

const populateTableData = (assumptions, title: string, type: string) => {
  const found = assumptions?.find((assumption) => assumption.type === type) || []
  return Object.entries(found).length ? generateTableRow(title, found) : []
}
</script>
